import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../../../utils/apiClient';
import { CodeDisplay } from './CodeDisplay';
import { SectionDisplay } from './SectionDisplay';
import { ArticleDisplay } from './ArticleDisplay';

interface LegifranceModalProps {
    isVisible: boolean;
    onClose: () => void;
    isLoading: boolean;
    codeId?: string;
    sectionId?: string;
    articleId?: string;
}

interface LegifranceData {
    code?: {
        id: string;
        title: string;
        description?: string;
    };
    section?: {
        id: string;
        title: string;
        description?: string;
    };
    // Type union pour gérer l'ancien et le nouveau format
    article?: any;
    // Nouvelles données enrichies
    hierarchie?: any;
    modifications?: any;
    references?: any;
}

const LegifranceModal: React.FC<LegifranceModalProps> = ({
    isVisible,
    onClose,
    isLoading: externalLoading,
    codeId,
    sectionId,
    articleId,
}) => {
    const [data, setData] = useState<LegifranceData>({});
    const [error, setError] = useState<string | null>(null);
    const [isLoading, setIsLoading] = useState(false);
    const [hasAttemptedFetch, setHasAttemptedFetch] = useState(false);
    const [activeTab, setActiveTab] = useState<'article' | 'historique' | 'references'>('article');

    const determineEndpoint = useCallback(() => {
        const endpoint =
            articleId && sectionId && codeId
                ? 'legifrance/details-article'
                : sectionId && codeId
                    ? 'legifrance/details-section'
                    : codeId
                        ? 'legifrance/details-code'
                        : null;
        return endpoint;
    }, [articleId, sectionId, codeId]);

    const fetchData = useCallback(async () => {
        const endpoint = determineEndpoint();
        if (!endpoint) {
            setError('Paramètres invalides pour la requête');
            return;
        }

        try {
            // Construire le body en fonction de l'endpoint
            let body: any = {};
            if (endpoint === 'legifrance/details-code') {
                body = { codeId };
            } else if (endpoint === 'legifrance/details-section') {
                body = { codeId, sectionId };
            } else if (endpoint === 'legifrance/details-article') {
                body = { articleId };
            }

            const response = await apiClient(`/${endpoint}`, {
                method: 'POST',
                body: JSON.stringify(body),
            });

            if (!response.ok) {
                const errorData = await response.json();
                throw new Error(
                    errorData.error?.message || 'Erreur lors de la récupération des détails'
                );
            }

            const responseData = await response.json();
            if (!responseData.success) {
                throw new Error(
                    responseData.error?.message || 'Erreur lors de la récupération des données'
                );
            }
            return responseData.data;
        } catch (error) {
            console.error('Erreur complète:', error);
            throw error;
        }
    }, [determineEndpoint, articleId, codeId, sectionId]);

    useEffect(() => {
        let isMounted = true;

        const loadData = async () => {
            if (!isVisible || hasAttemptedFetch) return;

            setIsLoading(true);
            setError(null);

            try {
                const result = await fetchData();
                if (isMounted) {
                    setData(result || {});
                    setHasAttemptedFetch(true);
                }
            } catch (error) {
                if (isMounted) {
                    setError(error instanceof Error ? error.message : 'Une erreur est survenue');
                }
            } finally {
                if (isMounted) {
                    setIsLoading(false);
                }
            }
        };

        loadData();

        return () => {
            isMounted = false;
        };
    }, [isVisible, fetchData, hasAttemptedFetch]);

    useEffect(() => {
        if (!isVisible) {
            setData({});
            setError(null);
            setHasAttemptedFetch(false);
            setActiveTab('article');
        }
    }, [isVisible]);

    const renderContent = () => {
        if (articleId && data.article) {
            return <ArticleDisplay data={data} activeTab={activeTab} />;
        }
        if (sectionId && data.code && data.section) {
            return (
                <>
                    <CodeDisplay code={data.code} />
                    <SectionDisplay section={data.section} />
                </>
            );
        }
        if (data.code) {
            return <CodeDisplay code={data.code} />
        }
        return (
            <div className="text-gray-500 text-sm">
                Aucune donnée disponible
            </div>
        );
    };

    if (!isVisible) return null;

    return (
        <div
            className="fixed inset-0 bg-black bg-opacity-20 z-30 transition-opacity duration-300"
            onClick={onClose}
        >
            <div
                className="legifrance-sidebar fixed inset-y-0 right-0 w-[400px] bg-mcdm-primary-100 dark:bg-mcdm-third-400 shadow-lg shadow-inner z-30 overflow-y-auto transform transition-transform duration-300"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="sticky pt-5 top-0 bg-mcdm-primary-100 dark:bg-mcdm-third-400 p-4 border-b border-mcdm-primary-200 dark:border-mcdm-primary-300 z-20">
                    <div className="flex justify-between items-center mb-2">
                        <h2 className="text-lg font-semibold text-mcdm-third-500 dark:text-mcdm-primary-100">
                            {articleId ? 'Article Légifrance' : sectionId ? 'Section Légifrance' : 'Code Légifrance'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-mcdm-third-500 dark:text-mcdm-primary-100"
                            aria-label="Fermer"
                        >
                            <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    
                    {/* Onglets pour les articles */}
                    {articleId && data.article && (
                        <div className="flex space-x-2 mt-3">
                            <button
                                onClick={() => setActiveTab('article')}
                                className={`px-3 py-1 text-xs rounded ${
                                    activeTab === 'article' 
                                        ? 'bg-mcdm-secondary-400 text-white' 
                                        : 'bg-mcdm-primary-200 text-mcdm-third-500 dark:bg-mcdm-third-300 dark:text-mcdm-primary-100'
                                }`}
                            >
                                Article
                            </button>
                            <button
                                onClick={() => setActiveTab('historique')}
                                className={`px-3 py-1 text-xs rounded ${
                                    activeTab === 'historique' 
                                        ? 'bg-mcdm-secondary-400 text-white' 
                                        : 'bg-mcdm-primary-200 text-mcdm-third-500 dark:bg-mcdm-third-300 dark:text-mcdm-primary-100'
                                }`}
                            >
                                Historique
                            </button>
                            <button
                                onClick={() => setActiveTab('references')}
                                className={`px-3 py-1 text-xs rounded ${
                                    activeTab === 'references' 
                                        ? 'bg-mcdm-secondary-400 text-white' 
                                        : 'bg-mcdm-primary-200 text-mcdm-third-500 dark:bg-mcdm-third-300 dark:text-mcdm-primary-100'
                                }`}
                            >
                                Références
                            </button>
                        </div>
                    )}
                </div>
                
                <div className="p-4">
                    {isLoading ? (
                        <div className="animate-pulse space-y-4">
                            <div className="h-8 bg-mcdm-primary-200 dark:bg-mcdm-primary-100 rounded w-3/4"></div>
                            <div className="h-4 bg-mcdm-primary-200 dark:bg-mcdm-primary-100 rounded w-1/2"></div>
                            <div className="h-32 bg-mcdm-primary-200 dark:bg-mcdm-primary-100 rounded w-full"></div>
                        </div>
                    ) : error ? (
                        <div className="text-red-600 text-sm p-4 bg-red-50 rounded">
                            {error}
                        </div>
                    ) : (
                        <>
                            <div className="space-y-4">
                                {renderContent()}
                            </div>
                            {/* Bouton "Voir sur Legifrance" */}
                            {articleId && data.article && (
                                <div className="pt-4">
                                   <a 
                                        href={`https://www.legifrance.gouv.fr/codes/article_lc/${data.article.id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-center px-4 py-2 bg-mcdm-secondary-400 dark:bg-mcdm-primary-100 hover:bg-mcdm-secondary-500 text-mcdm-primary-100 dark:text-mcdm-third-500 text-sm rounded-sm"
                                    >
                                        Voir sur Legifrance
                                    </a>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </div>
    );
};

export default LegifranceModal;